import shortid from 'shortid';

import PreferencesProvider, {
  DEFAULTS,
  PREFERENCES,
} from 'hcla-web-frontend-primitives/app/config/PreferencesProvider';
import React from 'react';
import {
  mxCell,
  mxCellState, mxConstants, mxEdgeHandler, mxEvent,
  mxGraph, mxGraphHandler,
  mxGraphLayout,
  mxGraphView, mxMorphing,
  mxOutline,
  mxRectangle,
  mxRubberband,
  mxGraphSelectionModel,
} from 'mxgraph-js';
import HCLTreeVertex from '../HCLTreeNode/HCLTreeVertex';
import Label, { LabelJSON } from '../HCLTreeNode/HCLTreeVertexValue/Label';
import HCLTreeMxVertexHandler from '../VertexHandler/HCLTreeMxVertexHandler';
import HCLTreeMxCellRenderer from '../HCLCellRenderer/HCLTreeMxCellRenderer';
import HCLMxGraphView from '../HCLMxGraphView/HCLMxGraphView';
import HCLMxGraphModel from '../HCLMxGraphModel/HCLMxGraphModel';
import HCLMxGraphSelectionModel from "../HCLMxGraphSelectionModel/HCLMxGraphSelectionModel";

interface HCLTreeMxGraphJSON {
  name: string;
  model_tree_id: number;
  label: LabelJSON;
}

/**
 * Base HCLTree class that extends {@link MxGraphType.mxGraph}.
 * Controls the lifecycle of the graph component.
 */
abstract class HCLTreeMxGraph extends mxGraph {
  private treeLabel: Label;

  private name: string;

  private modelTreeId: number;

  private readonly rubberBand: mxRubberband;

  private verticesMap: {[index: string]: HCLTreeVertex[]} = {};

  // @ts-ignore
  private preferencesProvider: PreferencesProvider = null;

  private outlineContainer: React.RefObject<HTMLScriptElement | HTMLDivElement> = null;

  private outline: mxOutline = null;

  private readonly notifyGraphMounted: () => void;

  private readonly notifyGraphUnmounted: () => void;

  // this will ignore provider
  private idsVisible: boolean;

  private descriptionEnabled: boolean;

  static NO_ANIMATION_VERTEX_THRESHOLD_COUNT = 5;

  /**
   * Construct the {@link MxGraph}, taking in callback functions for when the graph
   * gets mounted and unmounted
   *
   * @param {() => void} onGraphMounted
   * @param {() => void} onGraphUnmounted
   * @param {HCLMxGraphModel} model
   */
  constructor(onGraphMounted: () => void, onGraphUnmounted: () => void, model: HCLMxGraphModel) {
    // @ts-ignore
    super(null, model, mxConstants.DIALECT_STRICTHTML, null);
    this.renderHint = mxConstants.DIALECT_STRICTHTML;
    this.notifyGraphMounted = onGraphMounted;
    this.notifyGraphUnmounted = onGraphUnmounted;

    // if (!MxGraph.mxClient.IS_TOUCH || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0) {
    //   alert('You can touch me');
    // }
    this.rubberBand = new mxRubberband(this);
    // @ts-ignore
    this.rubberBand.defaultOpacity = 100;

    this.panningHandler.isPanningTrigger = (me: any) => true;

    mxConstants.HANDLE_FILLCOLOR = '#00f7ff'; // fills the selection handle nodes
    mxConstants.HANDLE_STROKECOLOR = '#cf0085'; // applied around selection handle
    mxConstants.VERTEX_SELECTION_COLOR = '#ffdf00';

    this.idsVisible = null;
    this.descriptionEnabled = true;
  }

  setLockEnabled(enabled: boolean): void {
    this.setEnabled(!enabled);
    this.setEventsEnabled(!enabled);
  }

  public isLockEnabled(): boolean {
    return !this.isEnabled() && !this.isEventsEnabled();
  }

  getRubberband(): mxRubberband {
    return this.rubberBand;
  }

  public getVerticesMap(): {[index: string]: HCLTreeVertex[]} {
    return this.verticesMap;
  }

  public getVerticesWithKey(key: string): HCLTreeVertex[] {
    const vertices = this.getVerticesMap()[key];
    return !vertices || !Object.keys(vertices).length ? [] : vertices;
  };

  /**
   * @return {string}
   */
  getName(): string {
    return this.name;
  }

  /**
   * @param {string} name
   */
  setName(name: string): void {
    this.name = name;
  }

  /**
   * @return {Label}
   */
  getTreeLabel(): Label {
    return this.treeLabel;
  }

  /**
   * @param {Label} label
   */
  setLabel(label: Label): void {
    this.treeLabel = label;
  }

  public setIDsVisible(visible: boolean): void {
    const provider = this.getPreferencesProvider();
    if (provider) {
      provider.setPreference(PREFERENCES.NODE_IDS_VISIBLE, visible)
        .then(() => {
          this.refresh(null);
        });
    }
  }

  // bypass preference
  public forceSetIdsVisible(visible: boolean) {
    this.idsVisible = visible;
    this.refresh(null);
  }

  public setSubLabelVisible(visible: boolean): void {
    const provider = this.getPreferencesProvider();
    if (provider) {
      provider.setPreference(PREFERENCES.NODE_VALUE_VISIBLE, visible)
        .then(() => {
          this.getVertices().forEach((vertex: HCLTreeVertex) => {
            vertex.updateShape(visible);
          });
          this.refresh(null);
        });
    }
  }

  public setDescriptionEnabled(enabled: boolean): void {
    const provider = this.getPreferencesProvider();
    if (provider) {
      provider.setPreference(PREFERENCES.NODE_DESCRIPTION_ENABLED, enabled)
        .then(() => {
          this.applyTooltipRules();
        });
    }
  }

  public getIDsVisible(): boolean {
    const provider = this.getPreferencesProvider();
    if (provider) {
      return Boolean(provider.getPreference(PREFERENCES.NODE_IDS_VISIBLE));
    }
    if (this.idsVisible !== null) {
      return this.idsVisible;
    }
    return Boolean(DEFAULTS.NODE_IDS_VISIBLE);
  }

  public isSubLabelVisible(): boolean {
    const provider = this.getPreferencesProvider();
    if (provider) {
      return Boolean(provider.getPreference(PREFERENCES.NODE_VALUE_VISIBLE));
    }
    return Boolean(DEFAULTS.NODE_VALUE_VISIBLE);
  }

  public getDescriptionEnabled(): boolean {
    const provider = this.getPreferencesProvider();
    if (provider) {
      return Boolean(provider.getPreference(PREFERENCES.NODE_DESCRIPTION_ENABLED));
    }
    if (this.descriptionEnabled !== null) {
      return this.descriptionEnabled;
    }
    return Boolean(DEFAULTS.NODE_DESCRIPTION_ENABLED);
  }

  public setOutlineVisible(visible: boolean): Promise<boolean> {
    const provider = this.getPreferencesProvider();
    if (provider) {
      return provider.setPreference(PREFERENCES.OUTLINE, visible)
        .then(() => {
          visible ? this.mountOutline() : this.unmountOutline();
          return visible;
        });
    } return null;
  }

  public getOutlineVisible(): boolean {
    const provider = this.getPreferencesProvider();
    if (provider) {
      return Boolean(provider.getPreference(PREFERENCES.OUTLINE));
    }
    return Boolean(DEFAULTS.OUTLINE);
  }

  /**
   * @return {MxGraphType.mxCell[]}
   */
  getVertices(): HCLTreeVertex[] {
    return Object.values(this.verticesMap).flat() as HCLTreeVertex[];
  }

  /**
   * @return {MxGraphType.mxCell[]}
   */
  getUnconnectedVertices(): HCLTreeVertex[] {
    return this.getVertices().filter(vertex => !vertex.edges);
  }

  /**
   * @param {number} id
   */
  setModeTreeId(id: number): void {
    this.modelTreeId = id;
  }

  /**
   * @return {number}
   */
  getModelTreeId(): number {
    return this.modelTreeId;
  }

  public addVertexWithUniqueKey(vertex: HCLTreeVertex, parent: mxCell = this.getDefaultParent(), transaction = true): HCLTreeVertex {
    const key = vertex.getKey();
    // generate a new unique key and assign it to the vertex, then add this vertex to the graph
    if (!key || !this.isUniqueKey(key)) {
      vertex.setKey(this.generateUniqueKey());
    }
    return this.addVertex(vertex, parent, transaction);
  }

  protected generateUniqueKey(): string {
    let id;
    do {
      id = HCLTreeMxGraph.generateShortID();
    } while (!this.isUniqueKey(id));
    return id;
  }

  /**
   * Generates a short ID (7-14 chars) with the following exceptions
   * - no HTTP unsafe - $, -, #, @,
   * - no * . ,
   */
  public static generateShortID(): string {
    shortid.characters('0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ(_');
    return shortid.generate();
  }

  protected isUniqueKey(key: string): boolean {
    if (!key) {
      return false;
    }

    if (!(key in this.verticesMap)) {
      return true;
    }

    if (this.verticesMap[key].length > 0) {
      return false;
    }
  }

  /**
   * @param {HCLTreeVertex} vertex
   * @param {MxGraphType.mxCell} parent
   * @param {boolean} transaction boolean representing no transaction
   * @return {HCLTreeVertex}
   */
  addVertex(vertex: HCLTreeVertex, parent: mxCell = this.getDefaultParent(), transaction = true): HCLTreeVertex {
    const model = this.getModel();
    try {
      if (transaction) {
        model.beginUpdate();
      }
      const key = vertex.getKey();
      if (!(key in this.verticesMap)) { this.verticesMap[key] = []; }
      this.verticesMap[key] = [...this.verticesMap[key], vertex];

      const addedCell = this.addCell(vertex, parent, null, null, null) as HCLTreeVertex;
      addedCell.onAddedToGraph(this);
      return addedCell;
    } catch (e) {
      console.error(e);
      if (transaction) {
        model.endUpdate();
      }
      return null;
    } finally {
      if (transaction) {
        model.endUpdate();
      }
    }
  }

  /**
   * @param {MxGraphType.mxCell} source
   * @param {MxGraphType.mxCell} target
   * @param {any} value
   * @param {boolean} transaction boolean representing no transaction
   * @return {MxGraphType.mxCell} edge that was added
   */
  public addEdgeBetween(source: mxCell, target: mxCell, value?: any, transaction = true): mxCell {
    const model = this.getModel();
    try {
      if (transaction) {
        model.beginUpdate();
      }
      return this.insertEdge(this.getDefaultParent(), null, value, source, target, null);
    } catch (e) {
      console.error(e);
      if (transaction) {
        model.endUpdate();
      }
      return null;
    } finally {
      if (transaction) {
        model.endUpdate();
      }
    }
  }

  /**
   * @param {MxGraphType.mxCell} edgeToAdd
   * @param {MxGraphType.mxCell} source
   * @param {MxGraphType.mxCell} target
   * @param {any} value
   * @param {boolean} transaction boolean representing no transaction
   * @return {MxGraphType.mxCell} edge that was added
   */
  public insertEdgeBetween(edgeToAdd: mxCell, source: mxCell, target: mxCell, value?: any, transaction = true): mxCell {
    const model = this.getModel();
    try {
      if (transaction) {
        model.beginUpdate();
      }
      return this.addEdge(edgeToAdd, this.getDefaultParent(), source, target);
    } catch (e) {
      console.error(e);
      if (transaction) {
        model.endUpdate();
      }
      return null;
    } finally {
      if (transaction) {
        model.endUpdate();
      }
    }
  }


  /**
   * @param vertex {HCLTreeVertex}
   * @param removeEdges {boolean}
   */
  removeVertex(vertex: HCLTreeVertex, removeEdges = true): mxCell[] {
    const model = this.getModel();
    let cells;
    try {
      model.beginUpdate();
      vertex.willRemoveFromGraph(this);
      this.verticesMap[vertex.getKey()] = this.verticesMap[vertex.getKey()].filter(v => v.id !== vertex.id);
      cells = this.removeCells([vertex], removeEdges);
    } catch (e) {
      console.error(e);
    } finally {
      model.endUpdate();
    }
    return cells;
  }

  /**
   * Remove all vertices in the graph. Useful when importing a model to an existing model.
   */
  removeAllVertices(): mxCell[] {
    const model = this.getModel();
    let cells;
    try {
      model.beginUpdate();
      this.verticesMap = {};
      cells = this.removeCells(this.getChildVertices(this.getDefaultParent()));
    } catch (e) {
      console.error(e);
    } finally {
      model.endUpdate();
    }
    return cells;
  }

  /**
   * The default rule for rendering cell values. If the cell is an instance of {@link HCLTreeVertex}, we will
   * return the label string from it's associated {@link HCLTreeVertexValue#getLabelName} function.
   * @param {MxGraphType.mxCell} cell - The input cell
   * @returns {string} - A string representing the label
   */
  convertValueToString(cell: mxCell): string {
    if (cell instanceof HCLTreeVertex) {
      const labelString = cell.getValue().getLabel().getName();
      return (!labelString) ? cell.getKey() : labelString;
    }
    return cell.value;
  }


  /**
   * Determine if cell is selectable
   * @param {boolean} cell - The cell to check
   * @returns {boolean} - Determination whether cell is selectable or not.
   */
  isCellSelectable(cell: boolean) { return this.isCellsSelectable() && !this.isCellLocked(cell); }

  /**
   * Cell is foldable if it has any outgoing edges
   * @param {MxGraphType.mxCell} cell - The cell to interrogate
   * @returns {boolean} - Whether cell is foldable or not
   */
  isCellFoldable(cell: mxCell) {
    return this.getModel().getOutgoingEdges(cell).length > 0;
  }


  selectCellsForEvent(cells: any, evt: any) {
    super.selectCellsForEvent(cells, evt);
  }

  /**
   * Returns an array of all mxConnectionConstraints for the given terminal. If the shape of the given terminal is a
   * <mxStencilShape> then the constraints of the corresponding mxStencil are returned.
   * @param terminal {@link MxGraphType.mxCellState} - mxCellState that represents the terminal.
   * @param source {@link boolean} - Specifies if the terminal is the source or target.
   */
  getAllConnectionConstraints = function(terminal: mxCellState, source?: boolean) {
    if (!terminal || !(terminal.cell instanceof HCLTreeVertex)) {
      return [];
    }
    const constraints = source ? (terminal.cell).getSourceConnectionConstraints() : (terminal.cell).getTargetConnectionConstraints();
    if (!constraints) {
      return mxGraph.prototype.getAllConnectionConstraints(terminal, source);
    } return constraints;
  };

  /**
   * Logic for folding the cells. Needs a concrete implementation of
   * {@link HCLTreeMxGraph.toggleSubTree}, which determines the logic for folding a subtree.
   * @param collapse Whether collapsing or not
   * @param recurse To recurse or not
   * @param cells All the cells in this {@link MxGraph} object
   */
  foldCells(collapse: boolean, recurse?: boolean, cells?: mxCell[], checkFoldable?: boolean, evt?: Event): mxCell[] {
    const model = this.getModel();
    model.beginUpdate();
    try {
      cells?.forEach((cell: mxCell) => {
        this.toggleSubTree(cell, !collapse);
        model.setCollapsed(cell, collapse);
      });
      this.applyDefaultLayout();
    } catch (e) {
      console.error(e);
    } finally {
      model.endUpdate();
    }

    return null;
  }

  applyStyles = () => {
    // set expanded/collapsed images to null by default
    this.collapsedImage = null;
    this.expandedImage = null;

    this.setHtmlLabels(true);
    this.setPanning(false);
    this.setAllowNegativeCoordinates(false);
    // @ts-ignore
    mxGraphHandler.prototype.guidesEnabled = true;
    // @ts-ignore
    mxEdgeHandler.prototype.snapToTerminals = true;
    // @ts-ignore
    mxOutline.prototype.updateOnPan = true;

    this.keepEdgesInBackground = true;

    // for editable cell label position
    this.cellEditor.init();
    // @ts-ignore
    this.cellEditor.textarea.style.position = 'absolute';

    // Defines the position of the folding icon
    // this.cellRenderer.getControlBounds = null;
    this.setStyles();
  };

  applyTooltipRules = () => {
    this.setTooltips(this.getDescriptionEnabled());
    // @ts-ignore
    this.tooltipHandler.ignoreTouchEvents = false;
    // @ts-ignore
    this.tooltipHandler.delay = 200;
  };

  applyValidationRules = () => {
    this.setCellsMovable(true);
    this.setCellsEditable(false);
    this.setConnectable(true);
    this.setCellsSelectable(true);
    this.setCellsDeletable(true);
    this.setVertexLabelsMovable(false);
    this.setCellsResizable(true);
    this.setAutoSizeCells(true);
    this.setAllowDanglingEdges(false);
    this.setDisconnectOnMove(false);
    this.setValidationRules();
  };

  /**
   * Applies the default layout provided by the concrete classes
   */
  applyDefaultLayout = (root?: HCLTreeVertex) => {
    this.applyLayout(this.getDefaultLayout(), root);
  };

  applyOverrides = () => {
  };

  /**
   * Applies the provided/configured layout to
   * @param layoutToApply
   * @param root
   */
  applyLayout = (layoutToApply: mxGraphLayout, root?: HCLTreeVertex) => {
    const graph = this;
    graph.getModel().beginUpdate();
    try {
      layoutToApply.execute(root || this.getDefaultParent());
    } catch (e) {
      console.error(e);
    } finally {
      if (Object.keys(this.verticesMap).length < HCLTreeMxGraph.NO_ANIMATION_VERTEX_THRESHOLD_COUNT) {
        // @ts-ignore
        const morph = new mxMorphing(graph);
        morph.addListener(mxEvent.DONE, () => {
          graph.getModel().endUpdate();
        });
        morph.startAnimation();
      } else {
        graph.getModel().endUpdate();
      }
    }
  };

  createVertexHandler(state: mxCellState): HCLTreeMxVertexHandler {
    return new HCLTreeMxVertexHandler(state);
  }

  public abstract createCellRenderer(): HCLTreeMxCellRenderer;

  public abstract createSelectionModel(): HCLMxGraphSelectionModel;

  /**
   * Attempt to mount the graph onto the container. If the container is not ready, or is null
   * then throw an error. If container exists then:
   * 1. init the graph within the container
   * 2. save the container reference
   * 3. initialize the graph with some common rules
   * 4. apply the styles associated with this graph type
   * 5. apply the validation rules associated with this graph type
   * 6. apply the default layout config
   * 7. call {@link onMountComplete}
   * @param container The container on which the graph will be mounted
   */
  mount = (container: HTMLScriptElement) => {
    if (!container) throw new Error('Cannot mount graph on null container');

    this.init(container);

    this.applyValidationRules();
    this.applyTooltipRules();
    this.applyStyles();
    this.applyOverrides();

    //  todo better implementation is to just create an event listener for when the outlineContainer hidden attr is toggled
    this.getOutlineVisible() ? this.mountOutline() : this.unmountOutline();

    this.onMountComplete();
    this.notifyGraphMounted();
    this.onNotifyGraphMounted();
  };

  /**
   * On unmount:
   * 1. Lose the reference to the container
   * 2. call {@link onUnmountComplete}
   */
  unmount = () => {
    this.onUnmountComplete();
    this.notifyGraphUnmounted();
  };

  protected setValidationRules() {
    // Defines the position of the folding icon
    this.cellRenderer.getControlBounds = state => this.getVertexControlBoundsRulesForCellState(state);
  }

  /**
   * @param {[index: string]: any} inputJSON
   */
  abstract populateFromJSON(inputJSON: {[index: string]: any}): void

  /**
   * @return {[index: string]: any}
   */
  abstract toJSON(): {[index: string]: any}

  abstract setStyles(): void

  /**
   * @param {MxGraphType.mxCell} cell
   * @param {boolean} collapse
   */
  abstract toggleSubTree(cell: mxCell, collapse: boolean): void

  /**
   * @return {MxGraphType.mxGraphLayout}
   */
  abstract getDefaultLayout(): mxGraphLayout

  abstract onUnmountComplete(): void

  abstract onMountComplete(): void

  abstract onNotifyGraphMounted(): void

  /**
   * Implemented by concrete classes to set rules for how a {@link MxCellRenderer} places control bounds
   * @param {MxGraphType.mxCellState} state - infer cell type, then set bounds-rules.
   * @return {MxGraphType.mxRectangle}
   */
  abstract getVertexControlBoundsRulesForCellState(state: mxCellState): mxRectangle

  /**
   *  @return {@link HCLTreeMxConnectionHandler}
   */
  // protected abstract createConnectionHandler(): HCLTreeMxConnectionHandler;

  createGraphView(): mxGraphView {
    return new HCLMxGraphView(this);
  }

  // @ts-ignore
  public setPreferencesProvider(provider: PreferencesProvider): void {
    this.preferencesProvider = provider;
  }

  // @ts-ignore
  protected getPreferencesProvider(): PreferencesProvider {
    return this.preferencesProvider;
  }

  public setOutlineContainer(container: React.RefObject<HTMLScriptElement | HTMLDivElement>) {
    this.outlineContainer = container;
    if (!this.outline) {
      this.mountOutline();
    } else {
      this.unmountOutline();
    }
  }

  public getOutlineContainer(): React.RefObject<HTMLScriptElement | HTMLDivElement> {
    return this.outlineContainer;
  }

  private mountOutline(): void {
    if (!this.outlineContainer || !this.outlineContainer.current) {
      return;
    }
    this.outlineContainer.current.hidden = false;
    if (!this.outline) {
      this.outline = new mxOutline(this, this.outlineContainer.current);
    }
    this.outline.setEnabled(true);
    this.outline.setZoomEnabled(true);
  }

  public isValidSource(cell: any): boolean {
    return !(cell.edge);
  }

  public isValidTarget(cell: any): boolean {
    return !(cell.edge);
  }

  private unmountOutline(): void {
    if (!this.outline) {
      return;
    }
    if (this.outlineContainer.current) {
      this.outlineContainer.current.hidden = true;
    }
    this.outline.setEnabled(false);
    this.outline.destroy();
    this.outline = null;
  }
}

export default HCLTreeMxGraph;
export { HCLTreeMxGraphJSON };
